import React, { useState, useRef, useEffect } from 'react';
import { Search, X, User } from 'lucide-react';

export interface ContactOption {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
}

export interface ContactAutocompleteMultiSelectProps {
  contacts: ContactOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const ContactAutocompleteMultiSelect: React.FC<ContactAutocompleteMultiSelectProps> = ({
  contacts,
  selected,
  onChange,
  placeholder = 'Type to search contacts...',
  className = '',
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedSet = new Set(selected);
  const q = query.toLowerCase().trim();
  const filtered = contacts
    .filter(
      (c) =>
        !selectedSet.has(c._id) &&
        (q.length === 0 ||
          (c.name || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q) ||
          (c.phone || '').replace(/\s/g, '').includes(q.replace(/\s/g, '')))
    )
    .slice(0, 12);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query, filtered.length]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (id: string) => {
    onChange([...selected, id]);
    setQuery('');
    setIsOpen(false);
  };

  const handleRemove = (id: string) => {
    onChange(selected.filter((x) => x !== id));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[highlightIndex]) {
      e.preventDefault();
      handleSelect(filtered[highlightIndex]._id);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex flex-wrap gap-2 mb-2">
        {selected.map((id) => {
          const c = contacts.find((x) => x._id === id);
          return (
            <span
              key={id}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
            >
              <User className="w-4 h-4" />
              <span>{c?.name || 'Client'}</span>
              {c?.email && <span className="text-xs opacity-75">({c.email})</span>}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(id)}
                  className="ml-1 p-0.5 rounded hover:bg-primary-200 dark:hover:bg-primary-800"
                  aria-label={`Remove ${c?.name || id}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          );
        })}
      </div>
      {!disabled && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            autoComplete="off"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
          {isOpen && (
            <ul
              ref={listRef}
              className="absolute z-50 mt-1 w-full max-h-48 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg"
            >
              {filtered.length === 0 ? (
                <li className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                  {query ? 'No matching contacts' : 'Type to search'}
                </li>
              ) : (
                filtered.map((c, i) => (
                  <li key={c._id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(c._id)}
                      className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-sm ${
                        i === highlightIndex ? 'bg-gray-100 dark:bg-gray-700' : ''
                      }`}
                    >
                      <User className="w-4 h-4 text-gray-400" />
                      <div className="flex flex-col gap-0.5">
                        <span>{c.name}</span>
                        {(c.email || c.phone) && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {[c.email, c.phone].filter(Boolean).join(' • ')}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
